import { Koi } from '@ksyos/koi';
import type { IAssets } from '@common/validation/assets';
import type { IPatientContactInfoDraft, IPatientContactInfoFinal } from '@common/validation/patientContactInfo';
import type { IPatientDeliveryInfo } from '@common/validation/patientDeliveryInfo';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import { tcohV2Conclusie } from '@common/customControls/TCOH/v2/tcohV2Conclusie';
import { tcohV1Conclusie } from '@common/customControls/tcohV1Conclusie';
import {
    ConclusieDiagnoseOption,
    EventEcgCardioExaminationDuration,
    Gender,
    HolterCardioExaminationDuration,
    TcohProcedureType,
} from '@common/enums';
import { assets } from '@common/validation/assets';
import { diagnosisSchema } from '@common/validation/diagnosis';
import { patientContactInfoDraft, patientContactInfoFinal } from '@common/validation/patientContactInfo';
import { patientDeliveryInfoDraft, patientDeliveryInfoFinal } from '@common/validation/patientDeliveryInfo';
import { afwijkingenV1, afwijkingenV1Draft } from './afwijkingen';

export const contraIndicatieOptions = ['Ja', 'Nee / Onbekend'];

export interface ICardioInspanningsECGRequestCriteriaValidated {
    bloeddruk_diastolisch: number;
    bloeddruk_systolisch: number;
    contra_indicaties: string;
    coronairSclerosePercentage: number;
    provocatie_klachten: boolean;
    retrosternale_pijnklachten: boolean;
    verdwijning_klachten: boolean;
}

export interface IBloodPressureObservation {
    bloeddruk_diastolisch?: number;
    bloeddruk_systolisch?: number;
    dateSent: Date | string;
    practitionerId: string;
}

export const cardioInspanningsEcgRequestCriteria = Koi.object({
    bloeddruk_diastolisch: Koi.number().max(110),
    bloeddruk_systolisch: Koi.number().max(200),
    contra_indicaties: Koi.string().max(255),
    coronairSclerosePercentage: Koi.number().min(0).max(70),
    provocatie_klachten: Koi.boolean(),
    retrosternale_pijnklachten: Koi.boolean(),
    verdwijning_klachten: Koi.boolean(),
});

const cardioInspanningsEcgRequestCriteriaDraft = cardioInspanningsEcgRequestCriteria.concat(
    Koi.object({
        bloeddruk_diastolisch: Koi.number().allow(null),
        bloeddruk_systolisch: Koi.number().allow(null),
        contra_indicaties: Koi.string().max(255).allow(null),
    })
);

// eslint-disable-next-line @stylistic/max-len
export const cardioInspanningsEcgRequestCriteriaSchema: ISchemaContainer<ICardioInspanningsECGRequestCriteriaValidated> =
    {
        schema: cardioInspanningsEcgRequestCriteria.options({ convert: false, presence: 'required' }),
    };

// eslint-disable-next-line @stylistic/max-len
export const cardioInspanningsEcgRequestCriteriaForProceedingSchema: ISchemaContainer<ICardioInspanningsECGRequestCriteriaValidated> =
    {
        schema: Koi.object({
            bloeddruk_diastolisch: Koi.number().max(110),
            bloeddruk_systolisch: Koi.number().max(200),
            contra_indicaties: Koi.string().valid(contraIndicatieOptions[1]),
            coronairSclerosePercentage: Koi.number().min(30).max(70),
        }).options({ convert: false, presence: 'required' }),
    };

interface ITiecgV1DraftRequestValidated {
    contactgegevens_patient: IPatientContactInfoDraft;
    gebruiktBetablokker: boolean | null;
    indicationOutcomeText: string | null;
    lichamelijkOnderzoek: string | null;
    opmerkingen: string | null;
    requestCriteriaData: ICardioInspanningsECGRequestCriteriaValidated;
    voorgeschiedenis_klachten: string | null;
}

export const tiecgV1FinalRequest = Koi.object({
    contactgegevens_patient: patientContactInfoFinal,
    gebruiktBetablokker: Koi.boolean(),
    indicationOutcomeText: Koi.string().max(255),
    lichamelijkOnderzoek: Koi.string().max(1000),
    opmerkingen: Koi.string().max(10000).allow(null),
    requestCriteriaData: cardioInspanningsEcgRequestCriteria,
    voorgeschiedenis_klachten: Koi.string().max(1000),
});

export interface ITiecgV1RequestValidated extends ITiecgV1DraftRequestValidated {
    contactgegevens_patient: IPatientContactInfoFinal;
    gebruiktBetablokker: boolean;
    indicationOutcomeText: string;
    lichamelijkOnderzoek: string;
    voorgeschiedenis_klachten: string;
}

export const tiecgV1RequestSchema: ISchemaContainer<ITiecgV1RequestValidated> = {
    schema: tiecgV1FinalRequest.options({ convert: false, presence: 'required' }),
};

export const tiecgV1DraftRequest = Koi.object({
    contactgegevens_patient: patientContactInfoDraft,
    gebruiktBetablokker: Koi.boolean().allow(null),
    indicationOutcomeText: Koi.string().max(255),
    lichamelijkOnderzoek: Koi.any().allow(null),
    opmerkingen: Koi.any().allow(null),
    requestCriteriaData: cardioInspanningsEcgRequestCriteriaDraft,
    voorgeschiedenis_klachten: Koi.any().allow(null),
});

export const tiecgV1DraftRequestSchema: ISchemaContainer<ITiecgV1DraftRequestValidated> = {
    schema: tiecgV1DraftRequest.options({ convert: false, presence: 'required' }),
};

export interface IKlachtenEcgCustomControlDataValidated {
    collaps: boolean | null;
    conditievermindering: boolean | null;
    dyspnoe: boolean | null;
    hartgeruis: boolean | null;
    klachten_sinds: string | null;
    klachten_tijdens_het_maken_van_het_ECG: boolean | null;
    klachtenvrij: boolean | null;
    moeheid: boolean | null;
    overig: string | null;
    palpitaties: boolean | null;
    pijn_op_de_borst: boolean | null;
    screening: boolean | null;
}

const klachtenEcgCustomControlData = Koi.object({
    collaps: Koi.boolean().allow(null),
    conditievermindering: Koi.boolean().allow(null),
    dyspnoe: Koi.boolean().allow(null),
    hartgeruis: Koi.boolean().allow(null),
    klachten_sinds: Koi.koi().date().allow(null),
    klachten_tijdens_het_maken_van_het_ECG: Koi.boolean().allow(null),
    klachtenvrij: Koi.boolean().allow(null),
    moeheid: Koi.boolean().allow(null),
    overig: Koi.string().max(1000).allow(null),
    palpitaties: Koi.boolean().allow(null),
    pijn_op_de_borst: Koi.boolean().allow(null),
    screening: Koi.boolean().allow(null),
});

export const klachtenEcgCustomControlDataSchema: ISchemaContainer<IKlachtenEcgCustomControlDataValidated> = {
    schema: klachtenEcgCustomControlData.options({ convert: false, presence: 'required' }),
};

export interface IRedenenEcgCustomControlDataValidated {
    algemeen_onderzoek: boolean | null;
    atypische_angina_pectoris: boolean | null;
    controle_therapie: boolean | null;
    decompensatio_cordis: boolean | null;
    diabetes_mellitus: boolean | null;
    duizeling_collapsneigingen_wegraking: boolean | null;
    dyspnoe_d_effort: boolean | null;
    familiaal_belast: boolean | null;
    geruststelling: boolean | null;
    hartfalen: boolean | null;
    hartkloppingen_overslag: boolean | null;
    hypercholesterolemie: boolean | null;
    hypertensie: boolean | null;
    kleplijden: boolean | null;
    overig: string | null;
    ritme_geleidingsstoornis: boolean | null;
    typische_angina_pectoris: boolean | null;
    ventrikel_hypertrofie: boolean | null;
    verdenking_ischemie_infarct: boolean | null;
}

const redenenEcgCustomControlData = Koi.object({
    algemeen_onderzoek: Koi.boolean().allow(null),
    atypische_angina_pectoris: Koi.boolean().allow(null),
    controle_therapie: Koi.boolean().allow(null),
    decompensatio_cordis: Koi.boolean().allow(null),
    diabetes_mellitus: Koi.boolean().allow(null),
    duizeling_collapsneigingen_wegraking: Koi.boolean().allow(null),
    dyspnoe_d_effort: Koi.boolean().allow(null),
    familiaal_belast: Koi.boolean().allow(null),
    geruststelling: Koi.boolean().allow(null),
    hartfalen: Koi.boolean().allow(null),
    hartkloppingen_overslag: Koi.boolean().allow(null),
    hypercholesterolemie: Koi.boolean().allow(null),
    hypertensie: Koi.boolean().allow(null),
    kleplijden: Koi.boolean().allow(null),
    overig: Koi.string().max(1000).allow(null),
    ritme_geleidingsstoornis: Koi.boolean().allow(null),
    typische_angina_pectoris: Koi.boolean().allow(null),
    ventrikel_hypertrofie: Koi.boolean().allow(null),
    verdenking_ischemie_infarct: Koi.boolean().allow(null),
});

export const redenenEcgCustomControlDataSchema: ISchemaContainer<IRedenenEcgCustomControlDataValidated> = {
    schema: redenenEcgCustomControlData.options({ convert: false, presence: 'required' }),
};

export interface IVoorgeschiedenisEcgCustomControlDataValidated {
    hartfalen: boolean | null;
    myocardinfarct: boolean | null;
    overig: string | null;
    PCI_CABG: boolean | null;
    perifeer_vaatlijden: boolean | null;
    ritmestoornis: boolean | null;
    TIA_CVA: boolean | null;
}

const voorgeschiedenisEcgCustomControlData = Koi.object({
    PCI_CABG: Koi.boolean().allow(null),
    TIA_CVA: Koi.boolean().allow(null),
    hartfalen: Koi.boolean().allow(null),
    myocardinfarct: Koi.boolean().allow(null),
    overig: Koi.string().max(1000).allow(null),
    perifeer_vaatlijden: Koi.boolean().allow(null),
    ritmestoornis: Koi.boolean().allow(null),
});

// eslint-disable-next-line @stylistic/max-len
export const voorgeschiedenisEcgCustomControlDataSchema: ISchemaContainer<IVoorgeschiedenisEcgCustomControlDataValidated> =
    {
        schema: voorgeschiedenisEcgCustomControlData.options({ convert: false, presence: 'required' }),
    };

export interface IRisicofactorenEcgCustomControlDataValidated {
    adipositas: boolean | null;
    cardiomyopathie: boolean | null;
    diabetes_mellitus: boolean | null;
    hypercholesterolemie: boolean | null;
    hypertensie: boolean | null;
    klepafwijkingen: boolean | null;
    myocardinfarct: boolean | null;
    onbekend: boolean | null;
    overig: string | null;
    pacemaker_implantatie: boolean | null;
    PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden: boolean | null;
    plotselinge_hartdood: boolean | null;
    positieve_familieanamnese_voor_hart_en_vaatziekten: boolean | null;
    reumatische_aandoening: boolean | null;
    roken: boolean | null;
}

const risicofactorenEcgCustomControlData = Koi.object({
    PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden: Koi.boolean().allow(null),
    adipositas: Koi.boolean().allow(null),
    cardiomyopathie: Koi.boolean().allow(null),
    diabetes_mellitus: Koi.boolean().allow(null),
    hypercholesterolemie: Koi.boolean().allow(null),
    hypertensie: Koi.boolean().allow(null),
    klepafwijkingen: Koi.boolean().allow(null),
    myocardinfarct: Koi.boolean().allow(null),
    onbekend: Koi.boolean().allow(null),
    overig: Koi.string().max(1000).allow(null),
    pacemaker_implantatie: Koi.boolean().allow(null),
    plotselinge_hartdood: Koi.boolean().allow(null),
    positieve_familieanamnese_voor_hart_en_vaatziekten: Koi.boolean().allow(null),
    reumatische_aandoening: Koi.boolean().allow(null),
    roken: Koi.boolean().allow(null),
});

export const risicofactorenEcgCustomControlDataSchema: ISchemaContainer<IRisicofactorenEcgCustomControlDataValidated> =
    {
        schema: risicofactorenEcgCustomControlData.options({ convert: false, presence: 'required' }),
    };

export interface ILichamelijkOnderzoekEcgCustomControlDataValidated {
    diastolisch_geruis: boolean | null;
    extra_tonen: boolean | null;
    hartafwijkingen: boolean | null;
    overig: string | null;
    pericardwrijven: boolean | null;
    systolisch_geruis: boolean | null;
}

const lichamelijkOnderzoekEcgCustomControlData = Koi.object({
    diastolisch_geruis: Koi.boolean().allow(null),
    extra_tonen: Koi.boolean().allow(null),
    hartafwijkingen: Koi.boolean().allow(null),
    overig: Koi.string().max(1000).allow(null),
    pericardwrijven: Koi.boolean().allow(null),
    systolisch_geruis: Koi.boolean().allow(null),
});

// eslint-disable-next-line @stylistic/max-len
export const lichamelijkOnderzoekEcgCustomControlDataSchema: ISchemaContainer<ILichamelijkOnderzoekEcgCustomControlDataValidated> =
    {
        schema: lichamelijkOnderzoekEcgCustomControlData.options({ convert: false, presence: 'required' }),
    };

export interface ITccrV1RequestValidated {
    conclusie_diagnose: ConclusieDiagnoseOption;
    files: IAssets;
    importedRestEcgData: IImportedRestEcgData | null;
    klacht: string | null;
    klachten: IKlachtenEcgCustomControlDataValidated | null;
    lab: string | null;
    lichamelijkOnderzoek: ILichamelijkOnderzoekEcgCustomControlDataValidated | null;
    previousCustomControlData: ITccrV1RequestValidated | null;
    reden: string | null;
    redenen: IRedenenEcgCustomControlDataValidated | null;
    risicofactoren: IRisicofactorenEcgCustomControlDataValidated | null;
    voorgeschiedenis: IVoorgeschiedenisEcgCustomControlDataValidated | null;
}

interface IImportedRestEcgData {
    afgerondOp: string | null;
    commentaar: string | null;
    HR: number | null;
    interpretatie: string | null;
    P: number | null;
    P_HOEK: number | null;
    PQ: number | null;
    QRS: number | null;
    QRS_HOEK: number | null;
    QT: number | null;
    QTc: number | null;
    T_HOEK: number | null;
}

const importedRestEcgData = Koi.object()
    .keys({
        HR: Koi.number().allow(null),
        P: Koi.number().allow(null),
        P_HOEK: Koi.number().allow(null),
        PQ: Koi.number().allow(null),
        QRS: Koi.number().allow(null),
        QRS_HOEK: Koi.number().allow(null),
        QT: Koi.number().allow(null),
        QTc: Koi.number().allow(null),
        T_HOEK: Koi.number().allow(null),
        afgerondOp: Koi.koi().date(),
        commentaar: Koi.string().max(1000).allow(null),
        interpretatie: Koi.string().max(1000).allow(null),
    })
    .allow(null);

const tccV1DraftRequest = Koi.object().keys({
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    files: assets,
    importedRestEcgData: importedRestEcgData,
    klacht: Koi.string().max(1000).allow(null),
    klachten: klachtenEcgCustomControlData.allow(null),
    lab: Koi.string().max(1000).allow(null),
    lichamelijkOnderzoek: lichamelijkOnderzoekEcgCustomControlData.allow(null),
    previousCustomControlData: Koi.object().allow(null),
    reden: Koi.string().max(1000).allow(null),
    redenen: redenenEcgCustomControlData.allow(null),
    risicofactoren: risicofactorenEcgCustomControlData.allow(null),
    voorgeschiedenis: voorgeschiedenisEcgCustomControlData.allow(null),
});

const tccV1FinalRequest = tccV1DraftRequest.concat(
    Koi.object().keys({
        klacht: Koi.string()
            .max(1000)
            .when('klachten', {
                is: null,
                otherwise: Koi.allow(null),
                then: Koi.when('previousCustomControlData', {
                    is: Koi.alternatives().try(
                        Koi.object({ klachten: Koi.required().disallow(null) }).unknown(true),
                        Koi.object({ klacht: Koi.required().disallow(null) }).unknown(true)
                    ),
                    otherwise: Koi.disallow(null),
                    then: Koi.allow(null),
                }),
            }),
        reden: Koi.string()
            .max(1000)
            .when('redenen', {
                is: null,
                otherwise: Koi.allow(null),
                then: Koi.when('previousCustomControlData', {
                    is: Koi.alternatives().try(
                        Koi.object({ redenen: Koi.required().disallow(null) }).unknown(true),
                        Koi.object({ reden: Koi.required().disallow(null) }).unknown(true)
                    ),
                    otherwise: Koi.disallow(null),
                    then: Koi.allow(null),
                }),
            }),
    })
);

export const tcchV1DraftRequest = tccV1DraftRequest;
export const tcciV1DraftRequest = tccV1DraftRequest;
export const tccrV1DraftRequest = tccV1DraftRequest;
export const tcchV1FinalRequest = tccV1FinalRequest;
export const tcciV1FinalRequest = tccV1FinalRequest;
export const tccrV1FinalRequest = tccV1FinalRequest;

export const tccV1DraftRequestSchema: ISchemaContainer<ITccrV1RequestValidated> = {
    schema: tccV1DraftRequest.options({ convert: false, presence: 'required' }),
};

const tcohV1ProcedureIndicatieControlData = Koi.object({
    algemeenOnderzoek: Koi.boolean(),
    andersText: Koi.string().max(1000).allow(null),
    atypischeAngina: Koi.boolean(),
    controleTherapie: Koi.boolean(),
    decompensatioCordis: Koi.boolean(),
    duizeling: Koi.boolean(),
    dyspnoeDeffort: Koi.boolean(),
    geruststelling: Koi.boolean(),
    hartfalen: Koi.boolean(),
    hartkloppingen: Koi.boolean(),
    hypertensie: Koi.boolean(),
    kleplijden: Koi.boolean(),
    ritmeGeleidingsstoornissen: Koi.boolean(),
    typischeAngina: Koi.boolean(),
    ventrikelHypertrofie: Koi.boolean(),
    verdenkingIschemie: Koi.boolean(),
});

export interface ITcohV2RequestAndProcedureIndicatieDataValidated {
    algemeenOnderzoek: boolean;
    andersText: string | null;
    atypischeAngina: boolean;
    controleTherapie: boolean;
    decompensatioCordis: boolean;
    duizeling: boolean;
    dyspnoeDeffort: boolean;
    geruststelling: boolean;
    hartfalen: boolean;
    hartkloppingen: boolean;
    hypertensie: boolean;
    kleplijden: boolean;
    ritmeGeleidingsstoornissen: boolean;
    typischeAngina: boolean;
    ventrikelHypertrofie: boolean;
    verdenkingIschemie: boolean;
}

const tcohV1ProcedureMedicatieControlData = Koi.object({
    aceRemmer: Koi.boolean(),
    andersText: Koi.string().max(1000).allow(null),
    antiAritmica: Koi.boolean(),
    antiHypertensiva: Koi.boolean(),
    antiStolling: Koi.boolean(),
    betaBlokker: Koi.boolean(),
    calciumantagonist: Koi.boolean(),
    digitalis: Koi.boolean(),
    diuretica: Koi.boolean(),
    geenCardialeMedicatie: Koi.boolean(),
    geenMedicatie: Koi.boolean(),
    nitraten: Koi.boolean(),
});

export interface ITcohV2RequestAndProcedureMedicatieDataValidated {
    aceRemmer: boolean;
    andersText: string | null;
    antiAritmica: boolean;
    antiHypertensiva: boolean;
    antiStolling: boolean;
    betaBlokker: boolean;
    calciumantagonist: boolean;
    digitalis: boolean;
    diuretica: boolean;
    geenCardialeMedicatie: boolean;
    geenMedicatie: boolean;
    nitraten: boolean;
}

type TcohRequestAndProcedureDraftValidated = {
    adviesBijAfwijkendeBeoordeling?: 'Ja' | 'Nee' | null;
    adviesBijAfwijkendeBeoordelingVraag?: string | null;
    afleveradres_patient?: IPatientDeliveryInfo;
    contactgegevens_patient: IPatientContactInfoDraft;
    eventEcgCardioExaminationDuration: EventEcgCardioExaminationDuration | null;
    geslachtHolter: Gender.male | Gender.female | null;
    gewicht: number | null;
    hasPacemaker?: string;
    holterCardioExaminationDuration: HolterCardioExaminationDuration | null;
    indicatie: ITcohV2RequestAndProcedureIndicatieDataValidated;
    lengte: number | null;
    medicatie: ITcohV2RequestAndProcedureMedicatieDataValidated;
    procedureType: TcohProcedureType | null;
};

type TcohRequestDraftValidated = TcohRequestAndProcedureDraftValidated & {
    bloeddruk_diastolisch: string | null;
    bloeddruk_systolisch: string | null;
    toelichting: string | null;
};

interface ITcohProcedureDraftValidated extends TcohRequestAndProcedureDraftValidated {
    bekendeEcgAfwijkingen: string | null;
    vraagstelling: string | null;
}

export type TcohRequestFinalValidated = TcohRequestDraftValidated & {
    contactgegevens_patient: IPatientContactInfoFinal;
    geslachtHolter: Gender.male | Gender.female;
    isServiceOrganizationSendingHomeHolter?: boolean;
    procedureType: TcohProcedureType;
    sendToDeliveryAddress?: boolean;
};

export interface ITcohProcedureFinalValidated extends ITcohProcedureDraftValidated {
    geslachtHolter: Gender.male | Gender.female;
    procedureType: TcohProcedureType;
}

export type TcohProcedureAndRequestFinalValidated = TcohRequestFinalValidated | ITcohProcedureFinalValidated;

export type TcohV2FinalDiagnosticReportAddAddendum = {
    advies?: string | null;
    conclusie?: tcohV2Conclusie;
    overigeOpmerkingen?: string | null;
    relatieEcgEnKlachten?: string | null;
};

const tcohV2RequestAndProcedureHomeHolterBase = Koi.object().keys({
    adviesBijAfwijkendeBeoordeling: Koi.string().valid('Ja', 'Nee').optional().allow(null),
    eventEcgCardioExaminationDuration: Koi.koi().when('procedureType', {
        is: TcohProcedureType.eventEcg,
        otherwise: Koi.allow(null),
        then: Koi.koi().enum(EventEcgCardioExaminationDuration),
    }),
    geslachtHolter: Koi.string().valid(Gender.male, Gender.female),
    gewicht: Koi.number().min(0).max(10000).allow(null),
    hasPacemaker: Koi.string().valid('Ja', 'Nee').optional(),
    holterCardioExaminationDuration: Koi.koi().when('procedureType', {
        is: TcohProcedureType.holter,
        otherwise: Koi.allow(null),
        then: Koi.koi().enum(HolterCardioExaminationDuration),
    }),
    indicatie: tcohV1ProcedureIndicatieControlData,
    isServiceOrganizationSendingHomeHolter: Koi.boolean().optional(),
    lengte: Koi.number().min(0).max(10000).allow(null),
    medicatie: tcohV1ProcedureMedicatieControlData,
    procedureType: Koi.koi().enum(TcohProcedureType),
    sendToDeliveryAddress: Koi.boolean().allow(null).optional(),
});

const tcohV2RequestAndProcedureHomeHolterDraft = Koi.object()
    .keys({
        adviesBijAfwijkendeBeoordelingVraag: Koi.when('adviesBijAfwijkendeBeoordeling', {
            is: 'Ja',
            otherwise: Koi.allow(null),
            then: Koi.string().max(10000).allow(null),
        }),
    })
    .concat(tcohV2RequestAndProcedureHomeHolterBase);

const tcohV2RequestAndProcedureHomeHolterFinal = Koi.object()
    .keys({
        adviesBijAfwijkendeBeoordelingVraag: Koi.when('adviesBijAfwijkendeBeoordeling', {
            is: 'Ja',
            otherwise: Koi.allow(null),
            then: Koi.string().max(10000),
        }),
    })
    .concat(tcohV2RequestAndProcedureHomeHolterBase);

const tcohV2FinalRequestAndProcedure = Koi.object()
    .keys({
        afleveradres_patient: Koi.when('isServiceOrganizationSendingHomeHolter', {
            is: true,
            otherwise: patientDeliveryInfoDraft.optional(),
            then: patientDeliveryInfoFinal,
        }),
        contactgegevens_patient: Koi.when('isServiceOrganizationSendingHomeHolter', {
            is: true,
            otherwise: patientContactInfoFinal.optional(),
            then: patientContactInfoFinal.concat(patientDeliveryInfoFinal),
        }),
    })
    .concat(tcohV2RequestAndProcedureHomeHolterFinal);

const tcohV2DraftRequestAndProcedure = Koi.object()
    .keys({
        afleveradres_patient: patientDeliveryInfoDraft.optional(),
        contactgegevens_patient: patientContactInfoDraft.concat(patientDeliveryInfoDraft).optional(),
        isServiceOrganizationSendingHomeHolter: Koi.boolean().allow(null).optional(),
        sendToDeliveryAddress: Koi.boolean().allow(null).optional(),
    })
    .concat(tcohV2RequestAndProcedureHomeHolterDraft)
    .optional()
    .concat(
        Koi.object().keys({
            eventEcgCardioExaminationDuration: Koi.koi()
                .when('procedureType', {
                    is: TcohProcedureType.eventEcg,
                    otherwise: Koi.allow(null),
                    then: Koi.koi().enum(EventEcgCardioExaminationDuration),
                })
                .allow(null),
            geslachtHolter: Koi.string().valid(Gender.male, Gender.female).allow(null),
            holterCardioExaminationDuration: Koi.koi()
                .when('procedureType', {
                    is: TcohProcedureType.holter,
                    otherwise: Koi.allow(null),
                    then: Koi.koi().enum(HolterCardioExaminationDuration),
                })
                .allow(null),
            procedureType: Koi.koi().enum(TcohProcedureType).allow(null),
        })
    );

const tcohV2Request = Koi.object({
    bloeddruk_diastolisch: Koi.numberAsString().min(0).max(500).allow(null),
    bloeddruk_systolisch: Koi.numberAsString().min(0).max(500).allow(null),
    files: assets.optional(),
    toelichting: Koi.string().max(10000).allow(null),
});

export const tcohV2FinalRequest = tcohV2FinalRequestAndProcedure.concat(tcohV2Request);
export const tcohV2DraftRequest = tcohV2DraftRequestAndProcedure.concat(tcohV2Request);

const tcohV2Procedure = Koi.object({
    bekendeEcgAfwijkingen: Koi.string().max(10000).allow(null),
    vraagstelling: Koi.string().max(10000).allow(null),
});

export const tcohV2FinalProcedureWithRequest = tcohV2FinalRequestAndProcedure.concat(tcohV2Procedure);
export const tcohV2DraftProcedureWithRequest = tcohV2DraftRequestAndProcedure.concat(tcohV2Procedure);

export const tcohV1FinalDiagnosticReport = Koi.object().keys({
    advies: Koi.string().max(10000).allow(null),
    afwijkingen: Koi.string().max(10000).allow(null),
    conclusie: Koi.koi().enum(tcohV1Conclusie),
    correlatieKlachten: Koi.string().max(10000).allow(null),
});

export const tcohV1DraftDiagnosticReport = tcohV1FinalDiagnosticReport.concat(
    Koi.object({ conclusie: Koi.any().allow(null) })
);

export const maxOverigeOpmerkingenLength = 10000;

const v2ConclusieSchema = Koi.koi().enum(tcohV2Conclusie);

export const tcohV2FinalDiagnosticReport = Koi.object().keys({
    advies: Koi.string().max(10000).allow(null),
    adviesConsult: Koi.object({
        advies: Koi.string().max(10000),
        diagnosis: diagnosisSchema,
        overigeOpmerkingen: Koi.string().max(10000),
    })
        .optional()
        .allow(null),
    conclusie: v2ConclusieSchema,
    overigeOpmerkingen: Koi.string().max(maxOverigeOpmerkingenLength).allow(null),
    relatieEcgEnKlachten: Koi.string().max(10000).allow(null),
});

export const tcohV2DraftDiagnosticReport = tcohV2FinalDiagnosticReport.concat(
    Koi.object({
        adviesConsult: Koi.object({
            advies: Koi.string().max(10000).allow(null, ''),
            diagnosis: diagnosisSchema.allow(null),
            overigeOpmerkingen: Koi.string().max(10000).allow(null, ''),
        })
            .optional()
            .allow(null),
        conclusie: v2ConclusieSchema.allow(null),
    })
);

export const tcohV2FinalDiagnosticReportAddAddendum = Koi.object()
    .keys({
        advies: Koi.string().max(10000).allow(null, '').optional(),
        conclusie: v2ConclusieSchema.optional(),
        overigeOpmerkingen: Koi.string().max(maxOverigeOpmerkingenLength).allow(null, '').optional(),
        relatieEcgEnKlachten: Koi.string().max(10000).allow(null, '').optional(),
    })
    .or('advies', 'conclusie', 'overigeOpmerkingen', 'relatieEcgEnKlachten');

export const tcohV2FinalDiagnosticReportAddAddendumSchema: ISchemaContainer<TcohV2FinalDiagnosticReportAddAddendum> = {
    schema: tcohV2FinalDiagnosticReportAddAddendum.options({ convert: false, presence: 'required' }),
};

const tiecgV1DiagnosticReportAdd = Koi.object({
    bloeddruk_response: Koi.string().max(255).allow(null),
    conclusie_inspanning_ecg: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen'),
    conclusie_rust_ecg: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen'),
    essentiele_veranderingen_in_het_ritme_geleiding_of_ST: Koi.string().valid('Ja', 'Nee').allow(null),
    hartfrequentierespons_op_inspanning_is: Koi.string().valid('Geëigend', 'Onvoldoende', 'Te groot').allow(null),
    toelichting: Koi.string()
        .max(255)
        .when('veranderingen_in_het_ECG_ritme', { is: 'Ja', otherwise: Koi.valid(null), then: Koi.allow(null) }),
    veranderingen_in_het_ECG_ritme: Koi.string().valid('Ja', 'Nee').allow(null),
});

export const tiecgV1FinalDiagnosticReport = tiecgV1DiagnosticReportAdd.concat(afwijkingenV1);
export const tiecgV1DraftDiagnosticReport = tiecgV1DiagnosticReportAdd
    .concat(
        Koi.object({
            conclusie_inspanning_ecg: Koi.any().allow(null),
            conclusie_rust_ecg: Koi.any().allow(null),
        })
    )
    .concat(afwijkingenV1Draft);

const tccV1DiagnosticReport = afwijkingenV1.concat(
    Koi.object({
        aanvullende_vragen: Koi.string().max(10000).allow(null),
        advies: Koi.when('bezoek_specialist_nodig', {
            is: 'Zie beleid',
            otherwise: Koi.string().max(10000).allow(null),
            then: Koi.string().max(10000).required(),
        }),
        bezoek_specialist_nodig: Koi.string().valid('Ja', 'Nee', 'Zie beleid'),
        conclusie: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen'),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
    })
);

const tccV1DraftDiagnosticReport = afwijkingenV1Draft.concat(
    Koi.object({
        aanvullende_vragen: Koi.string().allow(null).max(10000),
        advies: Koi.string().allow(null).max(10000),
        bezoek_specialist_nodig: Koi.string().valid('Ja', 'Nee', 'Zie beleid').allow(null),
        conclusie: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen').allow(null),
        conclusie_diagnose: Koi.any().allow(null),
    })
);

export const tccrV1FinalDiagnosticReport = tccV1DiagnosticReport;
export const tcchV1FinalDiagnosticReport = tccV1DiagnosticReport;
export const tcciV1FinalDiagnosticReport = tccV1DiagnosticReport;

export const tccrV1DraftDiagnosticReport = tccV1DraftDiagnosticReport;
export const tcchV1DraftDiagnosticReport = tccV1DraftDiagnosticReport;
export const tcciV1DraftDiagnosticReport = tccV1DraftDiagnosticReport;

interface ITiecgV1DraftProcedureValidated {
    bloeddruk_diastolisch: string | null;
    bloeddruk_systolisch: string | null;
    onderzoek_afgebroken: string | null;
}

interface ITiecgV1ProcedureValidated extends ITiecgV1DraftProcedureValidated {
    bloeddruk_diastolisch: string;
    bloeddruk_systolisch: string;
    onderzoek_afgebroken: string;
}

export const tiecgV1FinalProcedure = Koi.object({
    bloeddruk_diastolisch: Koi.string().numeric().max(3),
    bloeddruk_systolisch: Koi.string().numeric().max(3),
    onderzoek_afgebroken: Koi.string().valid('Ja', 'Nee'),
});

export const tiecgV1ProcedureSchema: ISchemaContainer<ITiecgV1ProcedureValidated> = {
    schema: tiecgV1FinalProcedure.options({ convert: false, presence: 'required' }),
};

export const tiecgV1DraftProcedure = tiecgV1FinalProcedure.concat(
    Koi.object({
        bloeddruk_diastolisch: Koi.any().allow(null),
        bloeddruk_systolisch: Koi.any().allow(null),
        onderzoek_afgebroken: Koi.any().allow(null),
    })
);

export const tcorV1DraftProcedureWithRequest = Koi.object().keys({
    files: assets,
    klacht: Koi.string().max(1000).allow(null),
    klachten: klachtenEcgCustomControlData.allow(null),
    lab: Koi.string().max(1000).allow(null),
    lichamelijkOnderzoek: lichamelijkOnderzoekEcgCustomControlData.allow(null),
    reden: Koi.string().max(1000).allow(null),
    redenen: redenenEcgCustomControlData.allow(null),
    risicofactoren: risicofactorenEcgCustomControlData.allow(null),
    voorgeschiedenis: voorgeschiedenisEcgCustomControlData.allow(null),
});

export const tcorV1FinalProcedureWithRequest = tcorV1DraftProcedureWithRequest.concat(
    Koi.object().keys({
        klacht: Koi.string()
            .max(1000)
            .when('klachten', { is: null, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
        reden: Koi.string()
            .max(1000)
            .when('redenen', { is: null, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
    })
);

export const tcorV1DraftRequest = Koi.object().keys({
    bloeddruk_diastolisch: Koi.numberAsString().min(0).max(500).allow(null),
    bloeddruk_systolisch: Koi.numberAsString().min(0).max(500).allow(null),
    contactgegevens_patient: patientContactInfoDraft,
    gewicht: Koi.number().min(0).max(10000).allow(null),
    klacht: Koi.string().max(1000).allow(null),
    klachten: klachtenEcgCustomControlData.allow(null),
    lengte: Koi.number().min(0).max(10000).allow(null),
    lichamelijkOnderzoek: lichamelijkOnderzoekEcgCustomControlData.allow(null),
    reden: Koi.string().max(1000).allow(null),
    redenen: redenenEcgCustomControlData.allow(null),
    risicofactoren: risicofactorenEcgCustomControlData.allow(null),
    toelichting: Koi.string().max(255).allow(null),
    voorgeschiedenis: voorgeschiedenisEcgCustomControlData.allow(null),
});

export const tcorV1FinalRequest = tcorV1DraftRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoFinal,
        klacht: Koi.string()
            .max(1000)
            .when('klachten', { is: null, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
        reden: Koi.string()
            .max(1000)
            .when('redenen', { is: null, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
    })
);

export const tcorV1FinalProcedure = Koi.object().keys({
    files: assets,
});

export const tcorV1DraftProcedure = tcorV1FinalProcedure;

export const tcorV1FinalDiagnosticReport = afwijkingenV1.concat(
    Koi.object().keys({
        conclusie: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen'),
    })
);

export const tcorV1DraftDiagnosticReport = afwijkingenV1Draft.concat(
    Koi.object().keys({
        conclusie: Koi.string().valid('Normaal', 'Afwijkend', 'Niet te beoordelen').allow(null),
    })
);
